<template>
  <div class="admin-page">
    <admin-page-header
      eyebrow="Finans"
      title="Cüzdan Raporları"
      description="Kazanç hareketlerini, işlem detaylarını ve kullanıcı satış toplamlarını takip edin."
    >
      <template #actions>
        <v-btn
          outlined
          color="primary"
          small
          :loading="loading || earningsLoading"
          @click="refresh"
        >
          Yenile
        </v-btn>
      </template>
    </admin-page-header>

    <v-alert
      v-if="errorMessage"
      dense
      outlined
      type="error"
      class="mb-4"
    >
      {{ errorMessage }}
    </v-alert>

    <admin-metric-strip
      :items="summaryCards"
      :loading="loading"
    />
    <v-tabs v-model="reportTab">
      <v-tab>Hareket defteri</v-tab><v-tab>Genel bakış</v-tab>
    </v-tabs>

    <v-row v-show="reportTab === 1">
      <v-col
        cols="12"
      >
        <v-card
          class="admin-table-card"
          outlined
        >
          <v-toolbar
            dense
            flat
            class="admin-page-toolbar"
          >
            <v-toolbar-title>Top kullanıcılar</v-toolbar-title>
          </v-toolbar>
          <v-data-table
            :headers="userHeaders"
            :items="overview.topUsers || []"
            :loading="loading"
            :footer-props="tableFooterProps"
            class="admin-data-table elevation-0"
          >
            <template v-slot:item.user="{ item }">
              <div>
                <div class="font-weight-bold">
                  <router-link
                    v-if="canOpenUser && item.userId"
                    :to="{ name: 'userList', query: { userId: String(item.userId), tab: '3' } }"
                  >
                    {{ item.fullName || `#${item.userId}` }}
                  </router-link><span v-else>{{ item.fullName || '-' }}</span>
                </div>
                <div class="text-caption text--secondary">
                  {{ item.email || '-' }}
                </div>
              </div>
            </template>
            <template v-slot:item.confirmedEarnings="{ item }">
              {{ formatMoney(item.confirmedEarnings) }}
            </template>
            <template v-slot:item.expectedEarnings="{ item }">
              {{ formatMoney(item.expectedEarnings) }}
            </template>
            <template v-slot:item.saleAmount="{ item }">
              {{ formatMoney(item.saleAmount) }}
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <v-card
      v-show="reportTab === 0"
      class="admin-table-card mt-6"
      outlined
    >
      <div class="admin-page-toolbar wallet-earnings-toolbar">
        <h2 class="text-h6">
          Kazanç hareketleri
        </h2>
        <v-select
          v-model="earningType"
          :items="earningTypes"
          label="Kazanç türü"
          outlined
          dense
          hide-details
          class="wallet-earnings-filter"
          @change="changeEarningType"
        ></v-select>
      </div>
      <v-form
        class="px-4 pt-3"
        @submit.prevent="applyEarningFilters"
      >
        <v-row dense>
          <v-col
            cols="12"
            sm="4"
            lg="2"
          >
            <v-text-field
              v-model="earningFilters.userId"
              label="Kullanıcı ID"
              type="number"
              min="1"
              outlined
              dense
              hide-details
            />
          </v-col>
          <v-col
            cols="6"
            sm="4"
            lg="2"
          >
            <v-text-field
              v-model="earningFilters.from"
              label="Başlangıç (Türkiye)"
              type="date"
              outlined
              dense
              hide-details
            />
          </v-col>
          <v-col
            cols="6"
            sm="4"
            lg="2"
          >
            <v-text-field
              v-model="earningFilters.to"
              label="Bitiş (Türkiye)"
              type="date"
              outlined
              dense
              hide-details
            />
          </v-col>
          <v-col
            cols="6"
            sm="4"
            lg="2"
          >
            <v-select
              v-model="earningFilters.status"
              :items="earningStatuses"
              label="Durum"
              clearable
              outlined
              dense
              hide-details
            />
          </v-col>
          <v-col
            cols="6"
            sm="4"
            lg="2"
          >
            <v-combobox
              v-model="earningFilters.source"
              :items="earningSources"
              label="Kaynak"
              clearable
              outlined
              dense
              hide-details
            />
          </v-col>
          <v-col
            cols="12"
            sm="4"
            lg="2"
            class="d-flex align-center"
          >
            <v-btn
              type="submit"
              color="primary"
              small
              :loading="earningsLoading"
            >
              Uygula
            </v-btn><v-btn
              icon
              title="Filtreleri temizle"
              aria-label="Kazanç filtrelerini temizle"
              @click="resetEarningFilters"
            >
              <v-icon>{{ clearIcon }}</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-form>
      <div class="px-4 pb-3 text-caption text--secondary">
        Tüm geçmiş kazanç kayıtları sayfalanır.
        <span v-if="!earningsLoading && !earningsError">Toplam {{ formatNumber(earningsTotal) }} kayıt.</span>
        Filtre yalnızca bu tabloyu etkiler; genel cüzdan özetleri değişmez.
        <span v-if="earningType === 'POINTS'">Puan filtresi cüzdana TL olarak yansıyan puan işlemlerini gösterir.</span>
      </div>
      <v-alert
        v-if="earningsError"
        dense
        outlined
        type="error"
        class="mx-4"
        role="alert"
      >
        {{ earningsError }}
        <v-btn
          text
          small
          :loading="earningsLoading"
          @click="fetchRecentEarnings"
        >
          Tekrar dene
        </v-btn>
      </v-alert>
      <v-data-table
        :headers="earningHeaders"
        :items="recentEarnings"
        :loading="earningsLoading"
        :options="earningOptions"
        :server-items-length="earningsTotal"
        :footer-props="{ 'items-per-page-options': [25, 50, 100] }"
        must-sort
        loading-text="Kazanç kayıtları yükleniyor..."
        class="admin-data-table elevation-0"
        @update:options="changeEarningOptions"
        @click:row="openEarningDetail"
      >
        <template #no-data>
          {{ earningsError ? 'Kazanç kayıtları yüklenemedi.' : 'Bu türde kazanç kaydı bulunamadı.' }}
        </template>
        <template v-slot:item.user="{ item }">
          <div>
            <div class="font-weight-bold">
              <router-link
                v-if="canOpenUser && item.userId"
                :to="{ name: 'userList', query: { userId: String(item.userId), tab: '3' } }"
                @click.native.stop
              >
                {{ item.userName || `#${item.userId}` }}
              </router-link><span v-else>{{ item.userName || '-' }}</span>
            </div>
            <div class="text-caption text--secondary">
              {{ item.email || '-' }}
            </div>
          </div>
        </template>
        <template v-slot:item.amount="{ item }">
          {{ formatMoney(item.amount) }}
        </template>
        <template v-slot:item.earningType="{ item }">
          {{ earningTypeLabel(item.earningType) }}
        </template>
        <template v-slot:item.saleAmount="{ item }">
          {{ formatMoney(item.saleAmount) }}
        </template>
        <template v-slot:item.entryDate="{ item }">
          {{ formatDateTime(item.entryDate) }}
        </template>
        <template v-slot:item.actions="{ item }">
          <v-btn
            text
            small
            color="primary"
            :aria-label="`İşlem #${item.id} detayını aç`"
            @click.stop="openEarningDetail(item)"
          >
            Detay
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog
      v-model="earningDetailDialog"
      max-width="1100"
      scrollable
      @input="onEarningDetailVisibility"
    >
      <v-card>
        <v-card-title class="wallet-detail-header">
          <div>
            <div class="text-h6">
              İşlem #{{ selectedEarningId }}
            </div>
            <div
              v-if="earningDetail"
              class="text-caption text--secondary"
            >
              {{ earningDetail.userName || 'Kullanıcı eşleşmesi yok' }}
              <span v-if="earningDetail.userId"> · #{{ earningDetail.userId }}</span>
              <span v-if="earningDetail.email"> · {{ earningDetail.email }}</span>
            </div>
          </div>
          <v-btn
            text
            small
            aria-label="İşlem detayını kapat"
            @click="closeEarningDetail"
          >
            Kapat
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <div
            v-if="earningDetailLoading"
            class="py-8 text-center"
            role="status"
          >
            <v-progress-circular
              indeterminate
              color="primary"
              size="28"
              class="mr-2"
            />
            İşlem detayı yükleniyor...
          </div>
          <v-alert
            v-else-if="earningDetailError"
            type="error"
            outlined
            role="alert"
          >
            {{ earningDetailError }}
            <v-btn
              text
              small
              @click="loadEarningDetail"
            >
              Tekrar dene
            </v-btn>
          </v-alert>
          <dl
            v-else-if="earningDetail"
            class="wallet-detail-grid"
          >
            <div
              v-for="row in earningDetailRows"
              :key="row.label"
              class="wallet-detail-field"
            >
              <dt>{{ row.label }}</dt>
              <dd>{{ row.value }}</dd>
            </div>
          </dl>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios'
import { mdiFilterRemoveOutline } from '@mdi/js'
import { getAccessToken, hasAdminAccess } from '@/utils/panelSession'
import { resolveApiErrorMessage } from '@/utils/apiErrors'
import AdminPageHeader from '@/components/admin/AdminPageHeader.vue'
import AdminMetricStrip from '@/components/admin/AdminMetricStrip.vue'
import walletTransactionDetailRows from '@/utils/walletTransactionDetails'

export default {
  components: {
    AdminPageHeader,
    AdminMetricStrip,
  },
  data: () => ({
    clearIcon: mdiFilterRemoveOutline,
    earningFilters: {
      userId: '', from: '', to: '', status: '', source: '',
    },
    appliedEarningFilters: {},
    earningStatuses: [{ text: 'Bekleyen', value: 'EXPECTED' }, { text: 'Kesinleşen', value: 'CONFIRMED' }, { text: 'İptal', value: 'CANCELLED' }],
    earningSources: ['REKLAMACTION', 'GELIRORTAKLARI', 'POINTS', 'CLICK_REWARD', 'SNAPSHOT_BACKFILL'],
    reportTab: 0,
    loading: false,
    errorMessage: '',
    earningType: 'ALL',
    earningOptions: {
      page: 1, itemsPerPage: 25, sortBy: ['entryDate'], sortDesc: [true],
    },
    earningsTotal: 0,
    recentEarnings: [],
    earningsLoading: false,
    earningsError: '',
    earningsRequest: 0,
    earningsCancel: null,
    earningDetailDialog: false,
    earningDetail: null,
    selectedEarningId: null,
    earningDetailLoading: false,
    earningDetailError: '',
    earningDetailRequest: 0,
    earningDetailCancel: null,
    earningTypes: [
      { text: 'Tümü', value: 'ALL' },
      { text: 'Tıklama', value: 'CLICK' },
      { text: 'Puan', value: 'POINTS' },
      { text: 'Satış', value: 'SALE' },
      { text: 'Nakit iade', value: 'CASHBACK' },
      { text: 'Düzeltme', value: 'ADJUSTMENT' },
      { text: 'Diğer', value: 'OTHER' },
    ],
    overview: {
      summary: {},
      topUsers: [],
      recentEarnings: [],
    },
    userHeaders: [
      { text: '#', value: 'rank', width: 60 },
      { text: 'Kullanıcı', value: 'user' },
      { text: 'Bekleyen', value: 'expectedEarnings', width: 130 },
      { text: 'Kesinleşen', value: 'confirmedEarnings', width: 130 },
      { text: 'Satış', value: 'saleCount', width: 100 },
      { text: 'Satış Tutarı', value: 'saleAmount', width: 140 },
      { text: 'Çekim', value: 'withdrawCount', width: 90 },
    ],
    earningHeaders: [
      { text: 'Kullanıcı', value: 'user' },
      { text: 'Kazanç türü', value: 'earningType', width: 130 },
      { text: 'Kampanya', value: 'campaignName' },
      { text: 'Unique ID', value: 'uniqueId' },
      { text: 'Tutar', value: 'amount', width: 120 },
      { text: 'Satış', value: 'saleAmount', width: 120 },
      { text: 'Durum', value: 'status', width: 120 },
      { text: 'Tarih', value: 'entryDate', width: 170 },
      {
        text: 'İşlem', value: 'actions', sortable: false, width: 90,
      },
    ],
  }),
  computed: {
    canOpenUser() { return hasAdminAccess() },
    earningDetailRows() {
      return walletTransactionDetailRows(this.earningDetail?.transaction, this)
    },
    requestOptions() {
      return {
        headers: { Authorization: `Bearer ${getAccessToken()}` },
      }
    },
    tableFooterProps() {
      return {
        'items-per-page-options': [10, 20, 50, 100],
      }
    },
    summaryCards() {
      const summary = this.overview.summary || {}

      return [
        {
          label: 'Toplam Cüzdan',
          value: this.formatNumber(summary.totalWallets),
          helper: 'Sistemdeki wallet kaydı',
        },
        {
          label: 'Expected Kazanç',
          value: this.formatMoney(summary.expectedEarnings),
          helper: 'Bekleyen ledger',
        },
        {
          label: 'Confirmed Kazanç',
          value: this.formatMoney(summary.confirmedEarnings),
          helper: 'Onaylı ledger',
        },
        {
          label: 'Pending Çekim',
          value: this.formatMoney(summary.pendingWithdrawAmount),
          helper: `${this.formatNumber(summary.pendingWithdrawCount)} istek`,
        },
        {
          label: 'Success Çekim',
          value: this.formatMoney(summary.successWithdrawAmount),
          helper: `${this.formatNumber(summary.successWithdrawCount)} istek`,
        },
        {
          label: 'Cancelled Kazanç',
          value: this.formatMoney(summary.cancelledEarnings),
          helper: 'İptal ledger',
        },
      ]
    },
  },
  mounted() {
    this.refresh()
  },
  beforeDestroy() {
    this.earningsRequest += 1
    if (this.earningsCancel) this.earningsCancel.cancel()
    this.closeEarningDetail()
  },
  methods: {
    openEarningDetail(item) {
      const id = Number(item?.id)
      if (!Number.isSafeInteger(id) || id <= 0) return undefined
      this.selectedEarningId = id
      this.earningDetailDialog = true

      return this.loadEarningDetail()
    },
    async loadEarningDetail() {
      const id = this.selectedEarningId
      if (!this.earningDetailDialog || !id) return
      this.earningDetailRequest += 1
      const request = this.earningDetailRequest
      if (this.earningDetailCancel) this.earningDetailCancel.cancel()
      this.earningDetailCancel = axios.CancelToken.source()
      this.earningDetailLoading = true
      this.earningDetailError = ''
      this.earningDetail = null
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_BASE_API_URL}admin/wallet-reports/earnings/${id}`,
          { ...this.requestOptions, cancelToken: this.earningDetailCancel.token, timeout: 30000 },
        )
        if (request !== this.earningDetailRequest) return
        const detail = response?.data?.data
        if (Number(detail?.transaction?.id) !== id) throw new Error('Invalid earning detail response')
        this.earningDetail = detail
      } catch (error) {
        if (request !== this.earningDetailRequest || axios.isCancel(error)) return
        this.earningDetailError = error?.response?.status === 404
          ? 'İşlem bulunamadı; kayıt kaldırılmış olabilir.'
          : resolveApiErrorMessage(error, 'İşlem detayı yüklenemedi.')
      } finally {
        if (request === this.earningDetailRequest) this.earningDetailLoading = false
      }
    },
    onEarningDetailVisibility(visible) {
      if (!visible) this.closeEarningDetail()
    },
    closeEarningDetail() {
      this.earningDetailRequest += 1
      if (this.earningDetailCancel) this.earningDetailCancel.cancel()
      this.earningDetailDialog = false
      this.earningDetail = null
      this.selectedEarningId = null
      this.earningDetailLoading = false
      this.earningDetailError = ''
    },
    applyEarningFilters() {
      const {
        userId, from, to, status, source,
      } = this.earningFilters
      if (userId && (!Number.isSafeInteger(Number(userId)) || Number(userId) <= 0)) {
        this.earningsError = 'Geçerli bir kullanıcı ID girin.'

        return undefined
      }
      if (from && to && from > to) {
        this.earningsError = 'Başlangıç tarihi bitişten sonra olamaz.'

        return undefined
      }
      this.appliedEarningFilters = Object.fromEntries(Object.entries({
        userId, from, to, status, source,
      }).filter(([, value]) => value !== '' && value != null))
      this.earningOptions = { ...this.earningOptions, page: 1 }

      return this.fetchRecentEarnings()
    },
    resetEarningFilters() {
      this.earningFilters = {
        userId: '', from: '', to: '', status: '', source: '',
      }; this.applyEarningFilters()
    },
    refresh() {
      return Promise.all([this.fetchOverview(), this.fetchRecentEarnings()])
    },
    changeEarningType() {
      this.earningOptions = { ...this.earningOptions, page: 1 }

      return this.fetchRecentEarnings()
    },
    changeEarningOptions(options) {
      const previous = this.earningOptions
      const sortBy = options.sortBy?.[0] || 'entryDate'
      const descending = options.sortDesc?.[0] !== false
      const size = [25, 50, 100].includes(options.itemsPerPage) ? options.itemsPerPage : 25
      const changedOrder = size !== previous.itemsPerPage
        || sortBy !== previous.sortBy[0] || descending !== previous.sortDesc[0]
      const page = changedOrder ? 1 : options.page
      if (!changedOrder && page === previous.page) return undefined
      this.earningOptions = {
        page, itemsPerPage: size, sortBy: [sortBy], sortDesc: [descending],
      }

      return this.fetchRecentEarnings()
    },
    earningTypeLabel(type) {
      return this.earningTypes.find(item => item.value === type)?.text || 'Diğer'
    },
    async fetchRecentEarnings() {
      this.earningsRequest += 1
      const request = this.earningsRequest
      if (this.earningsCancel) this.earningsCancel.cancel()
      this.earningsCancel = axios.CancelToken.source()
      this.earningsLoading = true
      this.earningsError = ''
      this.recentEarnings = []
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_BASE_API_URL}admin/wallet-reports/earnings`,
          {
            ...this.requestOptions,
            params: {
              type: this.earningType,
              ...this.appliedEarningFilters,
              page: this.earningOptions.page - 1,
              size: this.earningOptions.itemsPerPage,
              sortBy: this.earningOptions.sortBy[0],
              descending: this.earningOptions.sortDesc[0],
            },
            cancelToken: this.earningsCancel.token,
            timeout: 30000,
          },
        )
        if (request !== this.earningsRequest) return
        const result = response?.data?.data
        if (!Array.isArray(result?.content) || !Number.isFinite(result?.totalElements)) {
          throw new Error('Invalid earnings page response')
        }
        this.recentEarnings = result.content
        this.earningsTotal = result.totalElements
        this.earningOptions = { ...this.earningOptions, page: result.number + 1 }
      } catch (error) {
        if (request !== this.earningsRequest || axios.isCancel(error)) return
        this.earningsError = resolveApiErrorMessage(error, 'Kazanç kayıtları yüklenemedi.')
      } finally {
        if (request === this.earningsRequest) this.earningsLoading = false
      }
    },
    async fetchOverview() {
      this.loading = true
      this.errorMessage = ''
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_BASE_API_URL}admin/wallet-reports/overview`,
          this.requestOptions,
        )
        this.overview = response?.data?.data || {
          summary: {},
          topUsers: [],
          recentEarnings: [],
        }
      } catch (error) {
        this.errorMessage = error?.response?.data?.message || 'Cüzdan raporları yüklenemedi.'
        console.error('Wallet reports yüklenirken hata oluştu:', error)
      } finally {
        this.loading = false
      }
    },
    formatMoney(value) {
      const numeric = Number(value || 0)

      return `₺${new Intl.NumberFormat('tr-TR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(numeric)}`
    },
    formatNumber(value) {
      return new Intl.NumberFormat('tr-TR').format(Number(value || 0))
    },
    formatDateTime(value) {
      if (!value) return '-'
      const parsed = new Date(value)
      if (Number.isNaN(parsed.getTime())) return '-'

      return parsed.toLocaleString('tr-TR', {
        timeZone: 'Europe/Istanbul',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    formatDashboardStatus(status) {
      return { EXPECTED: 'Bekleyen', CONFIRMED: 'Kesinleşen', CANCELLED: 'İptal' }[status] || status || '-'
    },
    formatBoolean(value) {
      return value ? 'Evet' : 'Hayır'
    },
    formatPayout(value, type) {
      if (value === null || value === undefined || value === '') return '-'
      if (String(type || '').toLowerCase().includes('percent')) {
        return `%${new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 2 }).format(Number(value))}`
      }

      return this.formatMoney(value)
    },
  },
}
</script>

<style scoped>
.wallet-earnings-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  padding: 16px;
}

.wallet-earnings-filter {
  flex: 0 1 240px;
}

.wallet-detail-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  overflow-wrap: anywhere;
}

.wallet-detail-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 20px;
}

.wallet-detail-field dt {
  color: rgba(15, 23, 42, 0.6);
  font-size: 12px;
}

.wallet-detail-field dd {
  margin: 6px 0 0;
  color: rgba(15, 23, 42, 0.9);
  font-weight: 500;
  overflow-wrap: anywhere;
}

@media (max-width: 960px) {
  .wallet-detail-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}

@media (max-width: 600px) {
  .wallet-detail-grid { grid-template-columns: minmax(0, 1fr); }
  .wallet-earnings-filter {
    flex-basis: 100%;
  }
}
</style>
